import Container from '@atoms/Container';
import SvgSafeGatsbyImage from '@atoms/SvgSafeGatsbyImage';
import { wrap } from '@popmotion/popcorn';
import apCaseOnlyTitleTags from '@utils/apCaseOnlyTitleTags';
import paddingDefault from '@utils/paddingDefault';
import {
    animate, AnimatePresence, motion, PanInfo, Transition, useDragControls, useMotionValue,
} from 'framer-motion';
import React, { useState } from 'react';
import useResizeObserver from '@react-hook/resize-observer';
import ArrowTimelineSliderIcon from '../../images/svg/arrow-timeline-slider.svg';
import TimelinePointIcon from '../../images/svg/timeline-point.svg';
import { colors } from '../primitives/colors';
import { breakpoints } from '../primitives/tokens';
import Typography from '../primitives/typography';
import Divider from '../primitives/divider';
import Row from '../primitives/grid/row';
import Col from '../primitives/grid/col';

const variants = {
    enter: getDirection => ({
        x: getDirection() > 0 ? 1000 : -1000,
        opacity: 0,
    }),
    center: {
        zIndex: 1,
        x: 0,
        opacity: 1,
    },
    exit: getDirection => ({
        zIndex: 0,
        x: getDirection() < 0 ? 1000 : -1000,
        opacity: 0,
    }),
};

const swipeConfidenceThreshold = 10000;
const swipePower = (offset, velocity) => Math.abs(offset) * velocity;

const transition: Transition = {
    type: 'spring',
    bounce: 0,
};

const TimelineSlider = ({ data }) => {
    const directionRef = React.useRef<number>(0);

    const getDirection = (): number => directionRef.current;

    const [page, setPage] = useState(0);
    const { heading, slides, spacing } = data;

    const slideIndex = wrap(0, slides.length, page);

    const scrollRef = React.useRef<HTMLDivElement>(null);

    const paginate = (newDirection: number): void => {
        directionRef.current = newDirection;
        if (page + newDirection >= slides.length - 1) {
            setPage(0);
        } else if (page + newDirection === -1) {
            setPage(slides.length - 1);
        } else {
            setPage(page + newDirection);
        }
        // startDrag()
    };

    const x = useMotionValue(0);
    const containerRef = React.useRef<HTMLDivElement>(null);
    // Directly maps to page in context, is any number

    const itemWidth = 140;

    const getSlidesPerView = () : number => {
        const clientWidth = containerRef.current?.clientWidth || 0;
        return clientWidth / itemWidth;
    };

    const newX = (): number => ((-page * (containerRef.current?.clientWidth || 0)) / getSlidesPerView()); // + ((containerRef.current?.clientWidth || 0) / getSlidesPerView()) * 2;

    const handleEndDrag = (e: Event, dragProps: PanInfo): void => {
        e.preventDefault();
        e.stopPropagation();
        if (containerRef.current) {
            containerRef.current.classList.remove('dragging');
        }
        if (dragProps) {
            const clientWidth = containerRef.current?.clientWidth || 0;

            const { offset, velocity } = dragProps;

            if (Math.abs(velocity.y) > Math.abs(velocity.x)) {
                animate(x, newX(), transition);
                return;
            }

            const slidesPerView = getSlidesPerView();

            if (offset.x > clientWidth / 4 / slidesPerView) {
                // console.log('was a', offset.x / (clientWidth / totalSlidesAmount) / 2);
                paginate(-1);
            } else if (offset.x < -clientWidth / 4 / slidesPerView) {
                // console.log('was b');
                paginate(1);
            } else {
                animate(x, newX(), transition);
            }
        }
    };

    useResizeObserver<HTMLDivElement>(scrollRef, (entry) => {
        if (entry) {
            const animation = animate(x, newX(), transition);
            animation.complete();
        }
    });

    React.useEffect(() => {
        // console.log("run update", page, x, newX(), transition)
        const controls = animate(x, newX(), transition);
        return controls.stop;
    }, [page]);

    const focusedSlide = slides[slideIndex];
    const { copy, heading: slideHeading, image, year } = focusedSlide;

    return (
        <section css={paddingDefault(spacing)}>
            <div css={{
                overflow: 'hidden',
                position: 'relative',
            }}
            >
                <Container>

                    <Typography
                        as="h2"
                        fontSize={{
                            dt: '3xl',
                            tb: '2xl',
                            mb: 'xl',
                        }}
                        css={{
                            marginBottom: '36px',
                            [breakpoints.mb]: {
                                marginBottom: '24px',
                            },
                        }}
                    >
                        {apCaseOnlyTitleTags(heading)}
                    </Typography>
                    <Divider />
                    <div
                        css={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-end',
                            marginBottom: '36px',
                            marginTop: '16px',
                        }}
                        ref={scrollRef}
                    >
                        <button
                            type="button"
                            aria-label="Back"
                            css={{
                                position: 'relative',
                            }}
                            onClick={() => {
                                paginate(-1);
                            }}
                        >
                            <ArrowTimelineSliderIcon />
                        </button>
                        <div
                            css={{
                                overflow: 'hidden',
                                width: '100%',
                                position: 'relative',
                                marginBottom: '-2px',
                            }}
                        >
                            <motion.ul
                                css={{
                                    display: 'flex',
                                    padding: '0 20px',
                                    width: 'max-content',
                                    position: 'relative',
                                    transform: 'none!important',
                                    marginLeft: `calc(50% - ${itemWidth / 1.4}px)`,
                                }}
                                ref={containerRef}
                            >
                                {slides.map((slide, index) => (
                                    <motion.li
                                        css={{
                                            width: itemWidth,
                                            transition: 'opacity 0.3s',
                                            '&:after': {
                                                content: '""',
                                                width: itemWidth,
                                                height: '4px',
                                                backgroundColor: colors.primaryOrange,
                                                display: 'block',
                                                top: '-14px',
                                                position: 'relative',
                                                zIndex: '-1',
                                            },
                                        }}
                                        style={{
                                            x,
                                            opacity: slideIndex > index ? 0.4 : 1,
                                        }}
                                        draggable
                                        drag="x"
                                        dragElastic={1}
                                        onDragEnd={handleEndDrag}
                                        onDragStart={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }}
                                        key={slide.year}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (index > page) {
                                                    directionRef.current = 1;
                                                    setPage(index);
                                                } else {
                                                    directionRef.current = -1;
                                                    setPage(index);
                                                }
                                            }}
                                        >
                                            <div css={{ transition: '0.3s' }} style={{ fontWeight: slideIndex === index ? 'bold' : 'normal' }}>
                                                <Typography fontSize={{
                                                    dt: 'lg',
                                                }}
                                                >
                                                    {slide.year}
                                                </Typography>
                                            </div>
                                            <div
                                                css={{
                                                    svg: {
                                                        transition: '0.3s',
                                                        fill:
                                                            slideIndex === index
                                                                ? colors.primaryOrange : colors.shadesWhite,
                                                    },
                                                }}
                                            >
                                                <TimelinePointIcon />
                                            </div>
                                        </button>
                                    </motion.li>
                                ))}
                            </motion.ul>
                        </div>
                        <button
                            type="button"
                            aria-label="Forward"
                            css={{
                                position: 'relative',
                                transform: 'rotate(180deg)',
                            }}
                            onClick={() => {
                                paginate(1);
                            }}
                        >
                            <ArrowTimelineSliderIcon />
                        </button>
                    </div>
                    <Divider />
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={slideIndex.toString()}
                            custom={getDirection}
                            variants={variants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={{
                                x: { type: 'spring', stiffness: 300, damping: 30 },
                                opacity: { duration: 0.2 },
                            }}
                            drag="x"
                            dragConstraints={{ left: 0, right: 0 }}
                            dragElastic={1}
                            onDragEnd={(e, { offset, velocity }) => {
                                const swipe = swipePower(offset.x, velocity.x);

                                if (swipe < -swipeConfidenceThreshold) {
                                    paginate(1);
                                } else if (swipe > swipeConfidenceThreshold) {
                                    paginate(-1);
                                }
                            }}
                            css={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                position: 'relative',
                                padding: '32px 0',
                                [breakpoints.tb]: {
                                    alignItems: 'flex-start',
                                    flexDirection: 'column',
                                    paddingLeft: '0',
                                    paddingRight: '0',
                                },
                            }}
                        >
                            <Row>
                                <Col breakpoints={{
                                    dt: { span: 7 },
                                }}
                                >
                                    <div css={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        borderRight: `1px solid ${colors.grey100}`,
                                        height: '100%',
                                        paddingRight: '40px',
                                        [breakpoints.tb]: {
                                            marginBottom: '40px',
                                        },
                                        [breakpoints.mb]: {
                                            marginBottom: '20px',
                                        },
                                    }}
                                    >
                                        <Typography
                                            as="h3"
                                            fontSize={{
                                                dt: '3xl',
                                                tb: '2xl',
                                                mb: 'xl',
                                            }}
                                            css={{
                                                marginBottom: '24px',
                                                marginTop: '24px',
                                                [breakpoints.mb]: {
                                                    marginBottom: '12px',
                                                    marginTop: '0',
                                                },
                                            }}
                                        >
                                            {year}
                                        </Typography>
                                        <Divider />
                                        <Typography
                                            as="h3"
                                            fontSize={{
                                                dt: '2xl',
                                                tb: 'xl',
                                                mb: 'lg',
                                            }}
                                            css={{
                                                marginTop: '24px',
                                            }}
                                        >
                                            {slideHeading}
                                        </Typography>
                                        <Typography
                                            as="p"
                                            fontSize={{
                                                dt: 'xl',
                                                tb: 'lg',
                                                mb: 'base',
                                            }}
                                        >
                                            {copy}
                                        </Typography>
                                    </div>
                                </Col>
                                <Col breakpoints={{
                                    dt: { span: 5 },
                                }}
                                >
                                    {image?.url && (
                                        <div css={{
                                            // position: 'absolute',
                                            pointerEvents: 'none',
                                            inset: '0',
                                            width: '100%',
                                        }}
                                        >
                                            <SvgSafeGatsbyImage
                                                css={{
                                                    width: '100%',
                                                }}
                                                image={image}
                                                alt={image.description}
                                            />
                                        </div>
                                    )}
                                </Col>
                            </Row>
                        </motion.div>
                    </AnimatePresence>

                </Container>
            </div>
        </section>
    );
};

export default TimelineSlider;
